import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { systemService } from '../../services/system.service';
import { AppCheckAlertConfig } from '../../types/system';
import { getErrorMsg } from '../../utils/error';

const AppCheckAlertPage = () => {
  const { t } = useTranslation('common');
  const [config, setConfig] = useState<AppCheckAlertConfig>({
    email: '',
    noTokenThreshold: 0,
    invalidThreshold: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setConfig(await systemService.getAppCheckAlertConfig());
      } catch (e) {
        setError(getErrorMsg(e, 'Failed to load setting'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    // A threshold with no recipient saves fine and then silently never alerts —
    // the worst outcome here, because it looks configured. The API cannot reject
    // it (each field is valid alone), so the cross-field check lives here.
    if (!config.email && (config.noTokenThreshold > 0 || config.invalidThreshold > 0)) {
      setError(t('app_check_alert_email_required'));
      return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      setConfig(await systemService.setAppCheckAlertConfig(config));
      setSuccess(t('saved'));
    } catch (e) {
      setError(getErrorMsg(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  // Empty clears to 0 rather than NaN, so backspacing the field reads as "off".
  const setThreshold = (key: 'noTokenThreshold' | 'invalidThreshold') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '');
      setConfig((c) => ({ ...c, [key]: digits === '' ? 0 : parseInt(digits, 10) }));
    };

  return (
    <Layout title={t('app_check_alert_settings')}>
      <div className="space-y-6 max-w-2xl">
        {error && <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">{error}</div>}
        {success && <div className="p-3 rounded-md bg-green-50 text-green-600 border border-green-100">{success}</div>}

        <Card className="p-5 space-y-4">
          <div>
            <h2 className="text-base font-semibold">{t('app_check_alert_settings')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('app_check_alert_hint')}</p>
          </div>

          <Input
            label={t('app_check_alert_email')}
            value={config.email}
            disabled={loading}
            type="email"
            placeholder="admin@example.com"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setConfig((c) => ({ ...c, email: e.target.value.trim() }))
            }
          />

          <Input
            label={t('app_check_no_token_threshold')}
            value={String(config.noTokenThreshold)}
            disabled={loading}
            inputMode="numeric"
            onChange={setThreshold('noTokenThreshold')}
          />

          <Input
            label={t('app_check_invalid_threshold')}
            value={String(config.invalidThreshold)}
            disabled={loading}
            inputMode="numeric"
            onChange={setThreshold('invalidThreshold')}
          />

          <p className="text-xs text-gray-500 dark:text-gray-400">{t('app_check_alert_off_hint')}</p>
          <p className="text-xs text-amber-600 dark:text-amber-400">{t('app_check_alert_warning')}</p>

          <Button variant="primary" onClick={handleSave} disabled={loading || saving}>
            {saving ? t('saving') : t('save')}
          </Button>
        </Card>
      </div>
    </Layout>
  );
};

export default AppCheckAlertPage;
